'use client';

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart, Check, MessageCircle, Heart, X, Package, ZoomIn, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useAuthModal } from '../context/AuthModalContext';
import { useBike } from '../context/BikeContext';
import { useWishlist } from '../context/WishlistContext';
import { BikeSelectionModal } from '../components/BikeSelectionModal';
import ComboVariantSelector from '../components/ComboVariantSelector';
import { API } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  inferInventoryMode,
  getFilteredVariantStock,
  getUniqueColors,
  getUniqueSizes,
  getProductStock,
} from '../utils/productInventory';
import { getProductColorHex } from '../utils/productColors';
import { getProductTypeLabel, isLaunchLocked } from '../utils/productLaunch';
import { LaunchBadge, LaunchCountdown, useProductLaunchState } from '../components/LaunchCountdown';
import { trackProductView, trackCartEvent } from '../utils/analytics';
import { getImageUrl } from '../utils/imageUtils';

// Helper function to parse product description into structured sections
const parseDescription = (description) => {
  if (!description || typeof description !== 'string') {
    return {
      introduction: ['Product description not available.'],
      features: [],
      included: [],
      care: []
    };
  }

  // Split description into sections based on common patterns
  const sections = {
    introduction: [],
    features: [],
    included: [],
    care: []
  };

  // Try to parse structured format
  const lines = description.split('\n').filter(line => line.trim());
  let currentSection = 'introduction';
  let currentContent = [];

  lines.forEach(line => {
    const trimmed = line.trim();
    
    // Section headers
    if (trimmed.toLowerCase().includes('key features') || trimmed.toLowerCase().includes('features:')) {
      if (currentContent.length > 0) {
        sections[currentSection] = currentContent;
        currentContent = [];
      }
      currentSection = 'features';
    } else if (trimmed.toLowerCase().includes('what\'s included') || trimmed.toLowerCase().includes('included:') || trimmed.toLowerCase().includes('package includes')) {
      if (currentContent.length > 0) {
        sections[currentSection] = currentContent;
        currentContent = [];
      }
      currentSection = 'included';
    } else if (trimmed.toLowerCase().includes('care instructions') || trimmed.toLowerCase().includes('care:') || trimmed.toLowerCase().includes('maintenance')) {
      if (currentContent.length > 0) {
        sections[currentSection] = currentContent;
        currentContent = [];
      }
      currentSection = 'care';
    } else {
      // Content lines
      // Remove bullet points, numbers, and special characters
      const cleanLine = trimmed
        .replace(/^[•\-\*\+]\s*/, '')
        .replace(/^✓\s*/, '')
        .replace(/^\d+[\.\)]\s*/, '')
        .replace(/^📦\s*/, '')
        .replace(/^🔩\s*/, '')
        .replace(/^📖\s*/, '')
        .trim();
      
      if (cleanLine) {
        currentContent.push(cleanLine);
      }
    }
  });

  // Add remaining content
  if (currentContent.length > 0) {
    sections[currentSection] = currentContent;
  }

  // If no structured format was detected, treat entire description as introduction
  // Also split long paragraphs into sentences for better readability
  if (sections.introduction.length === 0 && sections.features.length === 0) {
    const fullText = lines.map(line => 
      line.replace(/^[•\-\*\+]\s*/, '')
         .replace(/^✓\s*/, '')
         .replace(/^\d+[\.\)]\s*/, '')
         .trim()
    ).filter(line => line).join(' ');
    
    // Split into paragraphs based on sentence structure
    if (fullText.length > 200) {
      const sentences = fullText.match(/[^.!?]+[.!?]+/g) || [fullText];
      let currentParagraph = '';
      const paragraphs = [];
      
      sentences.forEach(sentence => {
        if ((currentParagraph + sentence).length > 300) {
          if (currentParagraph) {
            paragraphs.push(currentParagraph.trim());
          }
          currentParagraph = sentence;
        } else {
          currentParagraph += sentence;
        }
      });
      
      if (currentParagraph) {
        paragraphs.push(currentParagraph.trim());
      }
      
      sections.introduction = paragraphs;
    } else {
      sections.introduction = [fullText];
    }
  }

  return sections;
};

const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, isAuthenticated } = useAuth();
  const { refreshCartCount } = useCart();
  const { openAuth } = useAuthModal();
  const { selectedBike, setBikeCompatibility } = useBike();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [adding, setAdding] = useState(false);
  const [isBikeModalOpen, setIsBikeModalOpen] = useState(false);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [cartVariations, setCartVariations] = useState([]);
  const [isComboVariantOpen, setIsComboVariantOpen] = useState(false);
  const [comboAdding, setComboAdding] = useState(false);
  const [isZoomOpen, setIsZoomOpen] = useState(false);
  const [zoomImage, setZoomImage] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [relatedLoading, setRelatedLoading] = useState(false);

  const getPremiumColorHex = getProductColorHex;

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    fetchProduct();
    
    // Track product view when product page is loaded
    if (id) {
      trackProductView(id);
    }
  }, [id]);

  useEffect(() => {
    if (product) {
      setIsWishlisted(isInWishlist(product.id));

      const mode = inferInventoryMode(product);
      if (mode === 'color_size') {
        const colors = getUniqueColors(product);
        if (colors.length > 0) {
          setSelectedColor(colors[0]);
          setSelectedImage(0); // Reset image when color is set
        } else {
          setSelectedColor('');
        }
      } else {
        setSelectedColor('');
      }

      if (mode !== 'size' && mode !== 'color_size') {
        setSelectedSize('');
      }
    }
  }, [product, isInWishlist]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
      setSelectedSize('');
      setSelectedColor('');
      setQuantity(1);
      setLoading(false);
      fetchRelatedProducts(response.data);
    } catch (error) {
      console.error('[v0] Failed to fetch product:', error);
      toast.error('Product not found');
      navigate('/products');
    }
  };

  const fetchRelatedProducts = async (currentProduct) => {
    if (!currentProduct) return;
    
    setRelatedLoading(true);
    try {
      // Determine related product types based on current product
      let relatedTypes = [];
      const productType = currentProduct.product_type || '';
      
      // Mapping for related products
      if (productType === 'accessory') {
        // For accessories, show other accessories, gears, and related items
        relatedTypes = ['accessory', 'gear'];
      } else if (productType === 'gear') {
        // For gears, show other gears, accessories, and jackets
        relatedTypes = ['gear', 'accessory'];
      } else if (productType === 'spare') {
        // For spares, show other spares
        relatedTypes = ['spare'];
      } else {
        // For others, show accessories and gears
        relatedTypes = ['accessory', 'gear'];
      }
      
      // Fetch products from related types
      const params = {
        limit: 8,
        exclude_id: currentProduct.id // Exclude current product
      };
      
      // Add product type filter if specified
      if (relatedTypes.length > 0) {
        params.product_type = relatedTypes[0]; // Use first type for simplicity
      }
      
      const response = await axios.get(`${API}/products`, { params });
      const products = Array.isArray(response.data) ? response.data : [];
      
      // Filter to only include products from related types and exclude current
      const filtered = products.filter(p => 
        p.id !== currentProduct.id && 
        relatedTypes.includes(p.product_type || '')
      );
      
      setRelatedProducts(filtered.slice(0, 8)); // Max 8 related products
    } catch (error) {
      console.error('Failed to fetch related products:', error);
      setRelatedProducts([]);
    } finally {
      setRelatedLoading(false);
    }
  };

  const addToCart = async () => {
    if (!isAuthenticated || !token) {
      openAuth('login');
      return;
    }

    if (!product) {
      toast.error('Product not loaded');
      return;
    }

    if (isLaunchLocked(product)) {
      toast.error(product.launch_badge || 'This product is launching soon');
      return;
    }

    if (requiresSize && !selectedSize) {
      toast.error('Please select a size');
      return;
    }

    if (requiresColor && !selectedColor) {
      toast.error('Please select a color');
      return;
    }

    if (product.product_type === 'spare' && !selectedBike) {
      setIsBikeModalOpen(true);
      return;
    }

    if (product.product_type === 'combo' || product.item_type === 'combo') {
      setIsComboVariantOpen(true);
      return;
    }

    try {
      setAdding(true);
      await axios.post(
        `${API}/cart/add`,
        {
          product_id: product.id,
          quantity: quantity,
          color: selectedColor || null,
          size: selectedSize || null,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success('Added to cart!');
      refreshCartCount();
      
      // Track cart event for analytics (non-blocking)
      trackCartEvent(product.id, quantity, "add");
    } catch (error) {
      console.error('Add to cart error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to add to cart';
      
      // Don't show stock error toast if product has variants - user needs to select different variant
      if (requiresColor || requiresSize) {
        if (errorMessage.toLowerCase().includes('stock') || errorMessage.toLowerCase().includes('out of stock')) {
          toast.error('Please select a different variant');
          return;
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setAdding(false);
    }
  };

  const buyNow = async () => {
    if (!isAuthenticated || !token) {
      openAuth('login');
      return;
    }

    if (!product) {
      toast.error('Product not loaded');
      return;
    }

    if (isLaunchLocked(product)) {
      toast.error(product.launch_badge || 'This product is launching soon');
      return;
    }

    if (requiresSize && !selectedSize) {
      toast.error('Please select a size');
      return;
    }

    if (requiresColor && !selectedColor) {
      toast.error('Please select a color');
      return;
    }

    if (product.product_type === 'spare' && !selectedBike) {
      setIsBikeModalOpen(true);
      return;
    }

    if (product.product_type === 'combo' || product.item_type === 'combo') {
      setIsComboVariantOpen(true);
      return;
    }

    try {
      setAdding(true);
      await axios.post(
        `${API}/cart/add`,
        {
          product_id: product.id,
          quantity: quantity,
          color: selectedColor || null,
          size: selectedSize || null,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      refreshCartCount();
      
      // Track cart event for analytics (non-blocking)
      trackCartEvent(product.id, quantity, "add");
      
      navigate('/cart');
    } catch (error) {
      console.error('Buy now error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to add to cart';
      
      // Don't show stock error toast if product has variants - user needs to select different variant
      if (requiresColor || requiresSize) {
        if (errorMessage.toLowerCase().includes('stock') || errorMessage.toLowerCase().includes('out of stock')) {
          toast.error('Please select a different variant');
          return;
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setAdding(false);
    }
  };

  const handleAddToCartClick = () => {
    console.log('Add to cart clicked for product:', product);
    if (!product) {
      toast.error('Product not loaded');
      return;
    }
    if (product.product_type === 'combo' || product.item_type === 'combo') {
      console.log('Opening combo variant selector for combo:', product);
      setIsComboVariantOpen(true);
    } else {
      addToCart();
    }
  };

  const handleComboAddToCart = async (selectedChildren) => {
    if (!product) {
      toast.error('Product not loaded');
      return;
    }
    if (!isAuthenticated || !token) {
      openAuth('login');
      return;
    }
    try {
      setComboAdding(true);
      await axios.post(
        `${API}/cart/add-combo`,
        {
          combo_id: product.id,
          quantity: quantity,
          selections: (selectedChildren || []).map((child) => ({
            product_id: child.product_id,
            size: child.size || null,
            color: child.color || null,
          })),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success('Combo added to cart!');
      refreshCartCount();
      setIsComboVariantOpen(false);
      
      // Track cart event for analytics (non-blocking) - track each item in combo
      if (selectedChildren) {
        selectedChildren.forEach(child => {
          trackCartEvent(child.product_id, child.quantity || 1);
        });
      }
    } catch (error) {
      console.error('Combo add to cart error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to add combo to cart';

      if (
        typeof errorMessage === 'string' &&
        (errorMessage.toLowerCase().includes('stock') ||
          errorMessage.toLowerCase().includes('out of stock') ||
          errorMessage.toLowerCase().includes('variant'))
      ) {
        toast.error(errorMessage);
        return;
      }

      toast.error(typeof errorMessage === 'string' ? errorMessage : 'Failed to add combo to cart');
    } finally {
      setComboAdding(false);
    }
  };

  const addVariationToCart = () => {
    if (!product) {
      toast.error('Product not loaded');
      return;
    }
    if (!selectedSize) {
      toast.error('Please select a size');
      return;
    }

    const newVariation = {
      size: selectedSize,
      color: selectedColor,
      quantity: quantity,
    };

    const exists = cartVariations.some(
      (v) => v.size === newVariation.size && v.color === newVariation.color
    );

    if (exists) {
      toast.error('This variation is already in your list');
      return;
    }

    setCartVariations([...cartVariations, newVariation]);
    setSelectedSize('');
    setSelectedColor('');
    setQuantity(1);
    toast.success('Variation added to list');
  };

  const removeVariation = (index) => {
    setCartVariations(cartVariations.filter((_, i) => i !== index));
  };

  const addAllVariationsToCart = async () => {
    if (!isAuthenticated || !token) {
      openAuth('login');
      return;
    }

    if (!product) {
      toast.error('Product not loaded');
      return;
    }

    try {
      setAdding(true);
      for (const variation of cartVariations) {
        await axios.post(
          `${API}/cart/add`,
          {
            product_id: product.id,
            quantity: variation.quantity,
            color: variation.color || null,
            size: variation.size || null,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }
      toast.success(`${cartVariations.length} variations added to cart!`);
      refreshCartCount();
      setCartVariations([]);
      
      // Track cart events for analytics (non-blocking) - track each variation
      cartVariations.forEach(variation => {
        trackCartEvent(product.id, variation.quantity);
      });
    } catch (error) {
      console.error('Add variations error:', error);
      toast.error(error.response?.data?.detail || 'Failed to add variations');
    } finally {
      setAdding(false);
    }
  };

  const inventoryMode = product ? inferInventoryMode(product) : 'simple';
  const variantStock = product && inventoryMode === 'color_size' ? getFilteredVariantStock(product) : [];
  const uniqueColors = product && inventoryMode === 'color_size' ? getUniqueColors(product) : [];
  const uniqueSizes = product ? getUniqueSizes(product, inventoryMode, variantStock) : [];
  const requiresSize = inventoryMode === 'size' || (inventoryMode === 'color_size' && uniqueSizes.length > 0);
  const requiresColor = inventoryMode === 'color_size' && uniqueColors.length > 0;
  const currentStock = getProductStock(product, inventoryMode, { selectedColor, selectedSize });
  
  // Get product images using the same logic as home page
  const [images, setImages] = useState(['/placeholder.svg']);
  
  useEffect(() => {
    if (!product) {
      setImages(['/placeholder.svg']);
      return;
    }
    
    if (inventoryMode === 'color_size' && product.colors?.length > 0 && product.color_images) {
      // Always use the first color by default, or selected color if available
      const colorToUse = selectedColor || product.colors[0];
      
      // Try exact match first
      if (product.color_images[colorToUse]?.length > 0) {
        setImages(product.color_images[colorToUse]);
        return;
      }
      
      // Try case-insensitive match
      const normalizedSearch = colorToUse.trim().toLowerCase();
      const matchingKey = Object.keys(product.color_images).find(
        key => key.trim().toLowerCase() === normalizedSearch
      );
      
      if (matchingKey && product.color_images[matchingKey]?.length > 0) {
        setImages(product.color_images[matchingKey]);
        return;
      }
      
      // If no match for selected color, try first available color
      const firstAvailableColorImages = Object.values(product.color_images).find(images => images?.length > 0);
      if (firstAvailableColorImages) {
        setImages(firstAvailableColorImages);
        return;
      }
    }
    
    setImages(product.images?.length > 0 ? product.images : ['/placeholder.svg']);
  }, [product, inventoryMode, selectedColor]);
  
  // Reset selected image when color changes to show first image of new color
  useEffect(() => {
    setSelectedImage(0);
  }, [selectedColor]);

  const handleZoom = (imageSrc) => {
    setZoomImage(getImageUrl(imageSrc));
    setIsZoomOpen(true);
  };

  const closeZoom = () => {
    setIsZoomOpen(false);
    setZoomImage(null);
  };

  const navigateImage = (direction) => {
    if (direction === 'next') {
      setSelectedImage((prev) => (prev + 1) % images.length);
    } else {
      setSelectedImage((prev) => (prev - 1 + images.length) % images.length);
    }
  };

  const launch = useProductLaunchState(product);
  const purchaseLocked = launch.locked;

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-[#ffcc32] font-[Arial] text-lg">
        Loading product...
      </div>
    );
  }

  if (!product) {
    return null;
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header spacing */}
      <div className="h-16 md:h-20" />

      {/* Main Product Section - 2 Column Layout */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          
          {/* LEFT COLUMN - Image Gallery */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-4"
          >
            {/* Main Image */}
            <div className="relative aspect-square overflow-hidden rounded-2xl border border-white/10 bg-[#080808] group">
              <img
                src={getImageUrl(images[selectedImage]) || "/placeholder.svg"}
                alt={product.name}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              
              {/* Zoom Button */}
              <button
                onClick={() => handleZoom(images[selectedImage])}
                className="absolute top-4 right-4 p-2 bg-black/50 backdrop-blur-sm rounded-full border border-white/20 hover:border-[#ffcc32] hover:bg-black/70 transition-all opacity-0 group-hover:opacity-100"
              >
                <ZoomIn size={20} className="text-white" />
              </button>

              <div className="absolute top-4 left-4 z-10 flex flex-col gap-2">
                <LaunchBadge product={product} />
                {product.is_outran && (
                  <div className="bg-red-600 text-white text-xs font-black uppercase px-2.5 py-1 rounded">
                    Outran
                  </div>
                )}
                {product.is_best_seller && (
                  <div className="bg-[#ffcc32] text-black text-xs font-black uppercase px-2.5 py-1 rounded">
                    Best Seller
                  </div>
                )}
                {product.is_new_arrival && (
                  <div className="bg-green-600 text-white text-xs font-black uppercase px-2.5 py-1 rounded">
                    New Arrival
                  </div>
                )}
              </div>

              {/* Navigation Arrows */}
              {images.length > 1 && (
                <>
                  <button
                    onClick={() => navigateImage('prev')}
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-black/50 backdrop-blur-sm rounded-full border border-white/20 hover:border-[#ffcc32] hover:bg-black/70 transition-all opacity-0 group-hover:opacity-100"
                  >
                    <ChevronLeft size={20} className="text-white" />
                  </button>
                  <button
                    onClick={() => navigateImage('next')}
                    className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-black/50 backdrop-blur-sm rounded-full border border-white/20 hover:border-[#ffcc32] hover:bg-black/70 transition-all opacity-0 group-hover:opacity-100"
                  >
                    <ChevronRight size={20} className="text-white" />
                  </button>
                </>
              )}
            </div>

            {/* Thumbnail Gallery */}
            {images.length > 1 && (
              <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
                {images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all
                      ${selectedImage === idx
                        ? 'border-[#ffcc32] ring-2 ring-[#ffcc32]/30'
                        : 'border-zinc-700 hover:border-zinc-500'
                      }`}
                  >
                    <img
                      src={getImageUrl(img)}
                      alt={`thumb-${idx}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          {/* RIGHT COLUMN - Product Information */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
          >
            {/* Product Title & Rating */}
            <div>
              <div className="flex flex-wrap items-center gap-2 text-sm text-zinc-400 mb-3 uppercase tracking-wider">
                <span className="text-[#ffcc32] font-semibold">
                  {product.product_type_label || getProductTypeLabel(product.product_type)}
                </span>
                {(product.spare_category_name || product.spare_category_slug) && (
                  <>
                    <span className="text-zinc-600">&gt;</span>
                    <span className="text-white font-semibold">
                      {product.spare_category_name || product.spare_category_slug}
                    </span>
                  </>
                )}
              </div>
              
              {/* Badges */}
              <div className="flex flex-wrap gap-2 mb-3">
                {product.is_outran && (
                  <div className="bg-red-600 text-white text-xs font-black uppercase px-3 py-1 rounded">
                    Outran
                  </div>
                )}
                {product.is_best_seller && (
                  <div className="bg-[#ffcc32] text-black text-xs font-black uppercase px-3 py-1 rounded">
                    Best Seller
                  </div>
                )}
                {product.is_new_arrival && (
                  <div className="bg-green-600 text-white text-xs font-black uppercase px-3 py-1 rounded">
                    New Arrival
                  </div>
                )}
              </div>
              
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white uppercase leading-tight mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {product.name}
              </h1>
              <div className="flex items-center gap-3">
                <div className="flex items-center text-yellow-500">
                  <span className="text-2xl">★</span>
                  <span className="text-white font-semibold text-base ml-1">{product.rating}</span>
                  {product.reviews_count > 0 && (
                    <span className="text-zinc-400 ml-2 text-sm">({product.reviews_count} Reviews)</span>
                  )}
                </div>
              </div>
            </div>

            <LaunchCountdown product={product} />

            {/* Price */}
            <div className="flex items-center gap-3">
              <span className="text-[#ffcc32] font-black text-3xl lg:text-4xl">
                Rs. {product.price.toLocaleString()}
              </span>
              {product.original_price && (
                <span className="text-zinc-500 line-through text-lg lg:text-xl">
                  Rs. {product.original_price.toLocaleString()}
                </span>
              )}
            </div>
            {product.original_price && (
              <div className="text-green-500 font-semibold text-sm">
                Save Rs. {(product.original_price - product.price).toLocaleString()} ({Math.round(((product.original_price - product.price) / product.original_price) * 100)}%)
              </div>
            )}

            {/* Combo Items List */}
            {(product.product_type === 'combo' || product.item_type === 'combo') && product.combo_items && product.combo_items.length > 0 && (
              <div className="rounded-2xl border border-white/10 bg-[#080808] p-4">
                <h3 className="text-white font-black uppercase text-sm mb-3">Combo Includes ({product.combo_items.length} items)</h3>
                <div className="space-y-3">
                  {product.combo_items.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between py-2 border-b border-white/5 last:border-0 cursor-pointer hover:bg-white/5 rounded-lg px-2 transition-colors"
                      onClick={() => item.product?.id && navigate(`/products/${item.product.id}`)}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <div className="w-16 h-16 bg-zinc-800 rounded-lg overflow-hidden flex-shrink-0">
                          {item.product?.images?.[0] ? (
                            <img
                              src={getImageUrl(item.product.images[0])}
                              alt={item.product.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package size={24} className="text-zinc-600" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="text-white font-semibold text-sm">{item.product?.name || 'Product'}</div>
                          <div className="text-zinc-400 text-xs">Qty: {item.quantity}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-[#ffcc32] font-semibold text-sm">
                          Rs. {item.product?.price ? (item.product.price * item.quantity).toLocaleString() : 'N/A'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {(() => {
                  const totalItemsValue = product.combo_items.reduce((sum, item) => {
                    return sum + (item.product?.price ? item.product.price * item.quantity : 0)
                  }, 0)
                  const savings = totalItemsValue - product.price
                  const savingsPercent = totalItemsValue > 0 ? Math.round((savings / totalItemsValue) * 100) : 0
                  
                  return (
                    <div className="mt-3 pt-3 border-t border-white/10">
                      <div className="flex justify-between items-center">
                        <span className="text-zinc-400 text-sm">Total Value:</span>
                        <span className="text-zinc-400 line-through text-sm">Rs. {totalItemsValue.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between items-center mt-1">
                        <span className="text-green-500 font-semibold text-sm">You Save:</span>
                        <span className="text-green-500 font-black text-sm">
                          Rs. {savings.toLocaleString()} ({savingsPercent}%)
                        </span>
                      </div>
                    </div>
                  )
                })()}
              </div>
            )}

            {/* Bike Compatibility */}
            {product.product_type === 'spare' && !selectedBike && (
              <div className="rounded-2xl border border-[#ffcc32]/30 bg-[#ffcc32]/5 p-4">
                <p className="text-white font-black uppercase text-sm">Add your bike to confirm compatibility</p>
                <p className="text-zinc-400 text-xs mt-1">This spare will be filtered for your exact model before ordering.</p>
                <button
                  onClick={() => setIsBikeModalOpen(true)}
                  className="mt-3 bg-[#ffcc32] text-black font-black uppercase px-5 py-2 rounded-full text-sm hover:bg-[#ffda5c] transition-all"
                >
                  Select Bike
                </button>
              </div>
            )}

            {selectedBike && (
              <div className="rounded-2xl border border-[#ffcc32]/30 bg-[#ffcc32]/5 p-4 text-[#ffefb0]">
                Selected bike: <span className="font-bold">{selectedBike.brand} {selectedBike.model} {selectedBike.variant}</span>
              </div>
            )}

            {/* Circular Color Swatches - Premium Design */}
            {requiresColor && (
              <div>
                <h3 className="text-white font-semibold text-sm uppercase mb-3 tracking-wider">Color</h3>
                <div className="flex flex-wrap gap-3">
                  {uniqueColors.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => {
                        setSelectedColor(color);
                        setSelectedImage(0);
                        setSelectedSize('');
                      }}
                      className={`relative w-12 h-12 rounded-full transition-all duration-300 transform
                        ${selectedColor === color
                          ? 'scale-110 ring-2 ring-[#ffcc32] ring-offset-2 ring-offset-black shadow-[0_0_20px_rgba(255,204,49,0.5)]'
                          : 'scale-100 hover:scale-110 hover:ring-2 hover:ring-white/30'
                        }
                      `}
                      style={{
                        backgroundColor: getPremiumColorHex(color),
                        boxShadow: selectedColor === color ? '0 0 25px rgba(255,204,49,0.6)' : 'none',
                      }}
                    >
                      {selectedColor === color && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Check size={16} className="text-black drop-shadow-md" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Premium Size Pills */}
            {requiresSize && (
              <div>
                <h3 className="text-white font-semibold text-sm uppercase mb-3 tracking-wider">Size</h3>
                <div className="flex flex-wrap gap-2">
                  {uniqueSizes.map((size) => {
                    const stock = inventoryMode === 'color_size'
                      ? variantStock
                          .filter((variant) =>
                            variant.size === size && (selectedColor ? variant.color === selectedColor : true)
                          )
                          .reduce((sum, variant) => sum + Number(variant.stock || 0), 0)
                      : Number(product.size_stock?.[size] || 0);
                    const isOut = stock === 0;

                    return (
                      <button
                        key={size}
                        type="button"
                        disabled={isOut}
                        onClick={() => {
                          setSelectedSize(size);
                          setQuantity(1);
                        }}
                        className={`px-5 py-2.5 rounded-full border font-semibold text-sm transition-all duration-300
                          ${selectedSize === size
                            ? 'bg-[#ffcc32] text-black border-[#ffcc32] shadow-[0_0_15px_rgba(255,204,49,0.4)]'
                            : 'bg-black text-white border-zinc-700 hover:border-[#ffcc32] hover:text-[#ffcc32]'
                          }
                          ${isOut ? 'opacity-40 cursor-not-allowed' : ''}
                        `}
                      >
                        {size}
                      </button>
                    );
                  })}
                </div>

                {selectedSize && !(requiresColor && uniqueSizes.length > 0) && (
                  <div className="mt-3 text-sm">
                    {currentStock === 0 ? (
                      <span className="text-red-500 font-semibold">Out of Stock</span>
                    ) : (
                      <span className="text-green-500 font-semibold">In Stock</span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Stock Status */}
            {product && (
              inventoryMode === 'simple' ||
              (inventoryMode === 'color_size' && requiresColor && !requiresSize && selectedColor) ||
              launch.locked
            ) && (
              <div className="flex items-center gap-2">
                {currentStock > 0 ? (
                  <>
                    <Check className="text-green-500" size={20} />
                    <span className="text-green-500 font-semibold">
                      In Stock
                    </span>
                  </>
                ) : (
                  <span className="text-red-500 font-semibold">Out of Stock</span>
                )}
              </div>
            )}

            {/* Quantity Selector */}
            <div className="flex items-center gap-4">
              <span className="text-white font-semibold text-sm uppercase tracking-wider">Quantity</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-10 h-10 bg-zinc-900 border border-zinc-700 text-white rounded-full hover:border-[#ffcc32] hover:text-[#ffcc32] transition-all flex items-center justify-center"
                >
                  -
                </button>
                <span className="w-12 text-center text-white font-bold text-lg">{quantity}</span>
                <button
                  onClick={() => setQuantity(Math.min(currentStock, quantity + 1))}
                  className="w-10 h-10 bg-zinc-900 border border-zinc-700 text-white rounded-full hover:border-[#ffcc32] hover:text-[#ffcc32] transition-all flex items-center justify-center"
                >
                  +
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3 pt-4 border-t border-zinc-800">
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={handleAddToCartClick}
                  disabled={
                    purchaseLocked ||
                    currentStock === 0 ||
                    adding ||
                    comboAdding ||
                    (requiresSize && !selectedSize) ||
                    (requiresColor && !selectedColor) ||
                    (product && product.product_type === 'spare' && !selectedBike)
                  }
                  className="flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-zinc-900 border border-white/15 text-white hover:border-[#ffcc32] hover:text-[#ffcc32] transition-all uppercase tracking-wider disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold"
                >
                  <ShoppingCart size={16} />
                  <span>
                    {purchaseLocked
                      ? (launch.badge || 'Coming Soon')
                      : (adding || comboAdding ? 'Adding...' : (cartVariations.length > 0 ? `Add ${cartVariations.length} Items` : 'Add to Cart'))}
                  </span>
                </button>
                <button
                  onClick={buyNow}
                  disabled={
                    purchaseLocked ||
                    currentStock === 0 ||
                    adding ||
                    (requiresSize && !selectedSize) ||
                    (requiresColor && !selectedColor) ||
                    (product && product.product_type === 'spare' && !selectedBike)
                  }
                  className="px-4 py-3 rounded-full bg-[#ffcc32] text-black font-bold uppercase tracking-wider hover:bg-[#ffda5c] transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm shadow-[0_0_20px_rgba(255,204,49,0.3)]"
                >
                  <span>{purchaseLocked ? (launch.badge || 'Coming Soon') : (adding ? 'Processing...' : 'Buy Now')}</span>
                </button>
              </div>

              {/* Wishlist Button */}
              <button
                onClick={async () => {
                  if (!isAuthenticated) {
                    openAuth('login');
                    return;
                  }
                  if (isWishlisted) {
                    const success = await removeFromWishlist(product.id);
                    if (success) {
                      setIsWishlisted(false);
                      toast.success('Removed from wishlist');
                    }
                  } else {
                    const success = await addToWishlist(product.id);
                    if (success) {
                      setIsWishlisted(true);
                      toast.success('Added to wishlist');
                    }
                  }
                }}
                className={`w-full py-3 border-2 rounded-full transition-colors text-sm font-semibold
                  ${isWishlisted
                    ? 'border-[#ffcc32] text-[#ffcc32] bg-[#ffcc32]/10'
                    : 'border-zinc-700 text-zinc-400 hover:border-[#ffcc32] hover:text-[#ffcc32]'
                  }
                `}
              >
                <div className="flex items-center justify-center gap-2">
                  <Heart size={16} fill={isWishlisted ? 'currentColor' : 'none'} />
                  <span>{isWishlisted ? 'Added to Wishlist' : 'Add to Wishlist'}</span>
                </div>
              </button>

              {/* WhatsApp CTA */}
              <a
                href="https://wa.me/919876543210"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-full hover:bg-green-500 transition-colors text-sm font-semibold"
              >
                <MessageCircle size={16} />
                <span>Ask RRIDE Expert</span>
              </a>
            </div>
          </motion.div>
        </div>
      </div>

      {/* FULL WIDTH SECTIONS - Description, Specifications, etc. */}
      {product && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          
          {/* Description Section */}
          <div className="mb-16">
            <h2 
              className="text-white font-bold uppercase tracking-wider mb-12"
              style={{ 
                fontFamily: 'Cormorant Garamond, serif',
                fontSize: 'clamp(1.75rem, 4vw, 2.25rem)'
              }}
            >
              Description
            </h2>
            
            <div 
              className="mx-auto description-content"
              style={{ 
                maxWidth: '900px',
                padding: 'clamp(1.25rem, 2vw, 3rem)',
                backgroundColor: '#000000'
              }}
            >
              <style>{`
                @media (max-width: 768px) {
                  .description-content {
                    padding: 20px !important;
                  }
                  .description-section {
                    margin-top: 32px !important;
                  }
                  .feature-item {
                    flex-direction: row !important;
                    align-items: flex-start !important;
                  }
                  .feature-text {
                    font-size: 15px !important;
                  }
                  .included-item {
                    padding: 12px !important;
                  }
                  .care-item {
                    padding: 12px !important;
                  }
                }
              `}</style>
              {/* Product Introduction */}
              <div className="mb-14 description-section" style={{ marginTop: 'clamp(2rem, 4vw, 3.5rem)' }}>
                <h3 
                  className="text-white font-semibold mb-6"
                  style={{ fontSize: 'clamp(1.25rem, 3vw, 1.5rem)' }}
                >
                  Product Introduction
                </h3>
                <div className="space-y-6" style={{ marginBottom: '24px' }}>
                  {parseDescription(product.description).introduction.map((paragraph, index) => (
                    <p 
                      key={index}
                      className="text-zinc-300 leading-relaxed"
                      style={{ 
                        fontSize: 'clamp(1rem, 2vw, 1.0625rem)',
                        lineHeight: '1.9',
                        letterSpacing: '0.2px',
                        maxWidth: '75ch',
                        marginBottom: '24px'
                      }}
                    >
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>

              {/* Divider */}
              <div 
                className="mb-14"
                style={{ 
                  borderBottom: '1px solid #27272A',
                  margin: 'clamp(2rem, 4vw, 3rem) 0'
                }}
              />

              {/* Key Features */}
              {parseDescription(product.description).features.length > 0 && (
                <div className="mb-14 description-section" style={{ marginTop: 'clamp(2rem, 4vw, 3.5rem)' }}>
                  <h3 
                    className="text-white font-semibold mb-6"
                    style={{ fontSize: 'clamp(1.25rem, 3vw, 1.5rem)' }}
                  >
                    Key Features
                  </h3>
                  <div 
                    className="space-y-4"
                    style={{ gap: '18px' }}
                  >
                    {parseDescription(product.description).features.map((feature, index) => (
                      <div 
                        key={index}
                        className="flex items-start gap-4 group transition-all duration-300 hover:translate-x-1 feature-item"
                      >
                        <div 
                          className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mt-0.5 transition-all duration-300 group-hover:scale-110"
                          style={{ backgroundColor: 'rgba(255, 204, 50, 0.15)' }}
                        >
                          <Check 
                            size={14} 
                            className="text-[#ffcc32] transition-all duration-300 group-hover:text-[#ffda5c]" 
                          />
                        </div>
                        <span 
                          className="text-zinc-300 leading-relaxed feature-text"
                          style={{ 
                            fontSize: 'clamp(1rem, 2vw, 1.0625rem)',
                            lineHeight: '1.9',
                            letterSpacing: '0.2px'
                          }}
                        >
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Divider */}
              {parseDescription(product.description).features.length > 0 && (
                <div 
                  className="mb-14"
                  style={{ 
                    borderBottom: '1px solid #27272A',
                    margin: 'clamp(2rem, 4vw, 3rem) 0'
                  }}
                />
              )}

              {/* What's Included */}
              {parseDescription(product.description).included.length > 0 && (
                <div className="mb-14 description-section" style={{ marginTop: 'clamp(2rem, 4vw, 3.5rem)' }}>
                  <h3 
                    className="text-white font-semibold mb-6"
                    style={{ fontSize: 'clamp(1.25rem, 3vw, 1.5rem)' }}
                  >
                    What's Included
                  </h3>
                  <div 
                    className="space-y-3"
                    style={{ gap: '18px' }}
                  >
                    {parseDescription(product.description).included.map((item, index) => {
                      // Determine icon based on item content
                      let icon = '📦';
                      if (item.toLowerCase().includes('strap') || item.toLowerCase().includes('hardware') || item.toLowerCase().includes('bolt') || item.toLowerCase().includes('screw')) {
                        icon = '🔩';
                      } else if (item.toLowerCase().includes('guide') || item.toLowerCase().includes('manual') || item.toLowerCase().includes('instruction')) {
                        icon = '📖';
                      } else if (item.toLowerCase().includes('cover') || item.toLowerCase().includes('bag') || item.toLowerCase().includes('case')) {
                        icon = '🎒';
                      }
                      
                      return (
                        <div 
                          key={index}
                          className="flex items-center gap-4 p-4 rounded-xl border transition-all duration-300 hover:bg-white/5 included-item"
                          style={{ 
                            borderColor: '#27272A',
                            backgroundColor: 'transparent'
                          }}
                        >
                          <span className="text-2xl">{icon}</span>
                          <span 
                            className="text-zinc-300"
                            style={{ 
                              fontSize: 'clamp(1rem, 2vw, 1.0625rem)',
                              lineHeight: '1.9'
                            }}
                          >
                            {item}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Divider */}
              {parseDescription(product.description).included.length > 0 && (
                <div 
                  className="mb-14"
                  style={{ 
                    borderBottom: '1px solid #27272A',
                    margin: 'clamp(2rem, 4vw, 3rem) 0'
                  }}
                />
              )}

              {/* Care Instructions */}
              {parseDescription(product.description).care.length > 0 && (
                <div className="description-section" style={{ marginTop: 'clamp(2rem, 4vw, 3.5rem)' }}>
                  <h3 
                    className="text-white font-semibold mb-6"
                    style={{ fontSize: 'clamp(1.25rem, 3vw, 1.5rem)' }}
                  >
                    Care Instructions
                  </h3>
                  <div 
                    className="space-y-3"
                    style={{ gap: '18px' }}
                  >
                    {parseDescription(product.description).care.map((instruction, index) => (
                      <div 
                        key={index}
                        className="flex items-start gap-4 p-4 rounded-xl border transition-all duration-300 hover:bg-white/5 care-item"
                        style={{ 
                          borderColor: '#27272A',
                          backgroundColor: 'transparent'
                        }}
                      >
                        <div 
                          className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mt-0.5"
                          style={{ backgroundColor: 'rgba(255, 204, 50, 0.15)' }}
                        >
                          <Check size={14} className="text-[#ffcc32]" />
                        </div>
                        <span 
                          className="text-zinc-300 leading-relaxed"
                          style={{ 
                            fontSize: 'clamp(1rem, 2vw, 1.0625rem)',
                            lineHeight: '1.9',
                            letterSpacing: '0.2px'
                          }}
                        >
                          {instruction}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Specifications */}
          {(product.specifications && Object.keys(product.specifications).length > 0) || product.weight ? (
            <div className="mb-16">
              <h2 className="text-white font-bold uppercase mb-8 tracking-wider" style={{ 
                fontFamily: 'Cormorant Garamond, serif',
                fontSize: 'clamp(1.625rem, 4vw, 2.125rem)' 
              }}>
                Specifications
              </h2>
              
              <div 
                className="border rounded-[20px] backdrop-blur-sm transition-all duration-250"
                style={{
                  backgroundColor: '#0D0D0F',
                  borderColor: '#27272A',
                  padding: 'clamp(1.25rem, 2vw, 3rem)'
                }}
              >
                {/* Desktop Layout */}
                <div className="hidden lg:block">
                  <div 
                    className="grid grid-cols-2 gap-9"
                    style={{ columnGap: '36px' }}
                  >
                    {Object.entries(product.specifications || {}).map(([key, value], index) => (
                      <div
                        key={key}
                        className="group relative flex items-center justify-between py-4 border-b transition-all duration-250 hover:bg-white/5"
                        style={{ 
                          borderColor: '#27272A',
                          minHeight: '68px'
                        }}
                      >
                        {/* Golden accent bar on hover */}
                        <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#ffcc32] opacity-0 transition-opacity duration-250 group-hover:opacity-100" />
                        
                        <span 
                          className="text-zinc-400 font-medium capitalize whitespace-nowrap pr-4"
                          style={{ fontSize: '15px' }}
                        >
                          {key.replace(/_/g, ' ')}
                        </span>
                        <span 
                          className="text-white font-semibold text-right leading-relaxed"
                          style={{ 
                            fontSize: '16px',
                            lineHeight: '1.7'
                          }}
                        >
                          {String(value)}
                        </span>
                      </div>
                    ))}
                    
                    {/* Weight if available */}
                    {product.weight && (
                      <div
                        className="group relative flex items-center justify-between py-4 border-b transition-all duration-250 hover:bg-white/5"
                        style={{ 
                          borderColor: '#27272A',
                          minHeight: '68px'
                        }}
                      >
                        <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#ffcc32] opacity-0 transition-opacity duration-250 group-hover:opacity-100" />
                        
                        <span 
                          className="text-zinc-400 font-medium capitalize whitespace-nowrap pr-4"
                          style={{ fontSize: '15px' }}
                        >
                          Weight
                        </span>
                        <span 
                          className="text-white font-semibold text-right leading-relaxed"
                          style={{ 
                            fontSize: '16px',
                            lineHeight: '1.7'
                          }}
                        >
                          {product.weight} kg
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Mobile Layout */}
                <div className="lg:hidden space-y-3">
                  {Object.entries(product.specifications || {}).map(([key, value]) => (
                    <div
                      key={key}
                      className="border-b pb-4"
                      style={{ borderColor: '#27272A' }}
                    >
                      <span 
                        className="text-zinc-400 font-medium capitalize block"
                        style={{ fontSize: '14px' }}
                      >
                        {key.replace(/_/g, ' ')}
                      </span>
                      <span 
                        className="text-white font-semibold block leading-relaxed"
                        style={{ 
                          fontSize: '15px',
                          lineHeight: '1.7',
                          marginTop: '8px'
                        }}
                      >
                        {String(value)}
                      </span>
                    </div>
                  ))}
                  
                  {/* Weight if available */}
                  {product.weight && (
                    <div
                      className="border-b pb-4"
                      style={{ borderColor: '#27272A' }}
                    >
                      <span 
                        className="text-zinc-400 font-medium capitalize block"
                        style={{ fontSize: '14px' }}
                      >
                        Weight
                      </span>
                      <span 
                        className="text-white font-semibold block leading-relaxed"
                        style={{ 
                          fontSize: '15px',
                          lineHeight: '1.7',
                          marginTop: '8px'
                        }}
                      >
                        {product.weight} kg
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : null}

          {/* Compatibility */}
          {product.compatibility && product.compatibility.length > 0 && (
            <div className="mb-16">
              <h2 className="text-2xl font-bold text-white uppercase mb-6 tracking-wider" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Compatible Bikes
              </h2>
              <div className="flex flex-wrap gap-2">
                {product.compatibility.map((bike, index) => (
                  <span
                    key={index}
                    className="px-4 py-2 bg-[#ffcc32]/10 border border-[#ffcc32]/30 text-[#ffcc32] text-sm rounded-full"
                  >
                    {bike.brand} {bike.model} {bike.variant}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Related Products */}
          {relatedProducts.length > 0 && (
            <div className="mb-16">
              <h2 className="text-2xl font-bold text-white uppercase mb-6 tracking-wider" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Related Products
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {relatedProducts.map((relatedProduct) => (
                  <div
                    key={relatedProduct.id}
                    onClick={() => navigate(`/products/${relatedProduct.id}`)}
                    className="bg-[#0b0b0b] border border-white/10 rounded-lg overflow-hidden cursor-pointer group hover:border-[#ffcc32]/50 transition-all"
                  >
                    <div className="aspect-square overflow-hidden bg-zinc-900">
                      <img
                        src={getImageUrl(relatedProduct.images?.[0] || relatedProduct.thumbnail || relatedProduct.image) || '/placeholder.svg'}
                        alt={relatedProduct.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-3">
                      <h3 className="text-white font-semibold text-sm line-clamp-2 mb-2">{relatedProduct.name}</h3>
                      <p className="text-[#ffcc32] font-bold">Rs. {relatedProduct.price?.toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Mobile Sticky Bottom Bar */}
      {product && (
        <div className="fixed bottom-0 left-0 right-0 z-50 lg:hidden border-t border-[#ffcc32]/25 bg-black/95 backdrop-blur-xl px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs text-zinc-400">{product.name}</p>
              <div className="flex items-center gap-2">
                <span className="text-lg font-black text-[#ffcc32]">
                  Rs. {product.price.toLocaleString()}
                </span>
                <span className="text-xs text-zinc-400">Qty {quantity}</span>
              </div>
            </div>
            <button
              onClick={handleAddToCartClick}
              disabled={
                purchaseLocked ||
                currentStock === 0 ||
                adding ||
                comboAdding ||
                (requiresSize && !selectedSize) ||
                (requiresColor && !selectedColor) ||
                (product.product_type === 'spare' && !selectedBike)
              }
              className="inline-flex rounded-full border border-white/15 px-3 py-3 text-xs font-black uppercase text-white disabled:opacity-50"
            >
              <ShoppingCart size={15} className="mr-1" />
              {adding || comboAdding ? 'Adding...' : 'Add'}
            </button>
            <button
              onClick={buyNow}
              disabled={
                purchaseLocked ||
                currentStock === 0 ||
                adding ||
                (requiresSize && !selectedSize) ||
                (requiresColor && !selectedColor) ||
                (product.product_type === 'spare' && !selectedBike)
              }
              className="rounded-full bg-[#ffcc32] px-5 py-3 text-xs font-black uppercase text-black disabled:opacity-50"
            >
              {purchaseLocked ? (launch.badge || 'Soon') : (adding ? 'Processing...' : 'Buy Now')}
            </button>
          </div>
        </div>
      )}

      {/* Image Zoom Modal */}
      <AnimatePresence>
        {isZoomOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 p-4"
            onClick={closeZoom}
          >
            <button
              onClick={closeZoom}
              className="absolute top-4 right-4 p-2 bg-black/50 rounded-full border border-white/20 hover:border-[#ffcc32] transition-all"
            >
              <X size={24} className="text-white" />
            </button>
            <img
              src={zoomImage}
              alt="Zoomed product"
              className="max-h-[90vh] max-w-[90vw] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modals */}
      <BikeSelectionModal
        isOpen={isBikeModalOpen}
        onClose={() => setIsBikeModalOpen(false)}
        onBikeSelect={(brand, model, variant) => {
          setBikeCompatibility(brand, model, variant);
          setIsBikeModalOpen(false);
        }}
      />

      <ComboVariantSelector
        open={isComboVariantOpen}
        onClose={() => setIsComboVariantOpen(false)}
        combo={product || {}}
        onConfirm={handleComboAddToCart}
        loading={comboAdding}
      />
    </div>
  );
};

export default ProductDetails;
